import React, { useState, useEffect, useCallback } from 'react';
import { Box, Tabs, Tab, Badge, Button } from '@mui/material';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectEffectiveModules } from '../redux/slices/authSlice';
import { selectActiveEntityId } from '../redux/slices/orgSlice';
import { hasModule } from '../data/serverModules';
import http from '../Api/http';
import { GetVacancies, GetCandidates, GetInterviews } from '../Api/Api';
import { PRIMARY, PRIMARY_LIGHT } from '../theme';
import { card, tonalBtn, ghostBtn } from '../components/uiKit';
import InterviewsTab from '../components/RecruitmentComps/InterviewsTab';
import CategoriesTab from '../components/RecruitmentComps/CategoriesTab';
import VacanciesTab from '../components/RecruitmentComps/VacanciesTab';
import CandidatesTab from '../components/RecruitmentComps/CandidatesTab';
import ActivityTab from '../components/RecruitmentComps/ActivityTab';
import OverviewTab from '../components/RecruitmentComps/OverviewTab';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import CareersSettingsTab from '../components/RecruitmentComps/CareersSettingsTab';

// Overview and Activity are reached from buttons in the breadcrumb row, not the tab strip.
const PRIMARY_BORDER = '#C9BEFB';
const PAGE_BTNS = ['overview', 'activity'];
const PIPELINE_STAGES = ['applied', 'interviewing', 'on-hold', 'selected'];

const TABS = [
    { key: 'overview', label: 'Overview', icon: InsightsRoundedIcon, module: 'recruitment-vacancies' },
    { key: 'categories', label: 'Job Categories', icon: CategoryRoundedIcon, module: 'recruitment-categories' },
    { key: 'vacancies', label: 'Vacancies', icon: WorkOutlineRoundedIcon, module: 'recruitment-vacancies' },
    { key: 'candidates', label: 'Candidates', icon: PeopleAltRoundedIcon, module: 'recruitment-candidates' },
    { key: 'interviews', label: 'Interviews', icon: EventAvailableRoundedIcon, module: 'recruitment-interviews' },
    { key: 'careers', label: 'Careers settings', icon: PublicRoundedIcon, module: 'careers-settings' },
    { key: 'activity', label: 'Activity', icon: HistoryRoundedIcon, anyModule: ['recruitment-vacancies', 'recruitment-candidates', 'recruitment-interviews'] },
];

export default function RecruitmentPage() {
    const [params, setParams] = useSearchParams();
    const modules = useSelector(selectEffectiveModules);
    const activeEntityId = useSelector(selectActiveEntityId);

    const shownTabs = TABS.filter((t) => (
        (!t.module || hasModule(modules, t.module))
        && (!t.anyModule || t.anyModule.some((m) => hasModule(modules, m)))
    ));
    const allowed = shownTabs.length ? shownTabs : TABS.slice(0, 1);
    const stripTabs = allowed.filter((t) => !PAGE_BTNS.includes(t.key));
    const visibleTabs = stripTabs.length ? stripTabs : allowed;
    const pageBtns = allowed.filter((t) => PAGE_BTNS.includes(t.key));
    const tabKey = allowed.some((t) => t.key === params.get('tab')) ? params.get('tab') : visibleTabs[0].key;
    const index = visibleTabs.findIndex((t) => t.key === tabKey); // -1 while a breadcrumb page is open
    const { setCrumbActions, setCrumb, setCrumbBack } = useOutletContext() || {};
    const openPage = pageBtns.find((t) => t.key === tabKey);

    const [kpi, setKpi] = useState({ openPositions: 0, openRoles: 0, today: 0, awaiting: 0, pipeline: 0, joined: 0, ready: false });
    const loadKpis = useCallback(async () => {
        const [vacRes, canRes, ivRes] = await Promise.allSettled([
            http.get(GetVacancies), http.get(GetCandidates), http.get(GetInterviews),
        ]);
        const bodyOf = (r) => (r.status === 'fulfilled' && !r.value?.data?.error ? r.value.data.data : null);
        const vac = bodyOf(vacRes); const can = bodyOf(canRes); const iv = bodyOf(ivRes);

        const vacancies = Array.isArray(vac?.vacancies) ? vac.vacancies : [];
        const openVac = vacancies.filter((v) => String(v.status ?? 'Open') !== 'Closed');
        const openPositions = openVac.reduce((s, v) => s + Math.max(0, (Number(v.openings) || 0) - (Number(v.filled) || 0)), 0);

        const candidates = Array.isArray(can?.candidates) ? can.candidates : [];
        const stageOf = (c) => String(c.stage ?? c.status ?? '').toLowerCase();
        const pipeline = candidates.filter((c) => PIPELINE_STAGES.includes(stageOf(c))).length;
        const joined = candidates.filter((c) => stageOf(c) === 'joined').length;

        const today = Array.isArray(iv?.today) ? iv.today.length : 0;
        const awaiting = Array.isArray(iv?.awaitingReview) ? iv.awaitingReview.length : 0;

        setKpi({ openPositions, openRoles: openVac.length, today, awaiting, pipeline, joined, ready: true });
    }, []);
    useEffect(() => { loadKpis(); }, [loadKpis, activeEntityId]);

    // Inside Overview/Activity the breadcrumb ends with that page's name, and the
    // Recruitment crumb leads back to the default (first) tab.
    useEffect(() => {
        if (!setCrumb || !setCrumbBack) return undefined;
        setCrumb(openPage ? openPage.label : null);
        setCrumbBack(openPage ? '/dashboard/recruitment' : null);
        return () => { setCrumb(null); setCrumbBack(null); };
    }, [openPage?.key, setCrumb, setCrumbBack]); // eslint-disable-line react-hooks/exhaustive-deps

    // Extra breadcrumb controls published by the Overview tab (its applied-date range picker).
    const [overviewActions, setOverviewActions] = useState(null);
    // Search + Add Candidate published by the Candidates tab.
    const [candidatesActions, setCandidatesActions] = useState(null);
    // Import published by the Vacancies tab.
    const [vacanciesActions, setVacanciesActions] = useState(null);
    // Add Category published by the Categories tab.
    const [categoriesActions, setCategoriesActions] = useState(null);
    // Search + Schedule Interview published by the Interviews tab.
    const [interviewsActions, setInterviewsActions] = useState(null);

    // Clicking the open page's button returns to the first tab.
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(pageBtns.length || (tabKey === 'overview' && overviewActions) || (tabKey === 'candidates' && candidatesActions) || (tabKey === 'vacancies' && vacanciesActions) || (tabKey === 'categories' && categoriesActions) || (tabKey === 'interviews' && interviewsActions) ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {tabKey === 'overview' && overviewActions}
                {tabKey === 'candidates' && candidatesActions}
                {tabKey === 'vacancies' && vacanciesActions}
                {tabKey === 'interviews' && interviewsActions}
                {pageBtns.map((t) => {
                    const on = tabKey === t.key;
                    return (
                        <Button
                            key={t.key} startIcon={<t.icon sx={{ color: PRIMARY }} />}
                            onClick={() => setParams({ tab: on ? visibleTabs[0].key : t.key }, { replace: true })}
                            sx={on
                                ? { ...tonalBtn, height: 42, px: 2, border: `1px solid ${PRIMARY}` }
                                : { ...ghostBtn, height: 42, px: 2, color: '#334155', ...(t.key === 'activity' && { border: `1px solid ${PRIMARY}` }), '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_BORDER, color: PRIMARY } }}
                        >
                            {t.label}
                        </Button>
                    );
                })}
                {tabKey === 'categories' && categoriesActions}
            </Box>
        ) : null);
        return () => setCrumbActions(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabKey, pageBtns.map((t) => t.key).join(), setCrumbActions, overviewActions, candidatesActions, vacanciesActions, categoriesActions, interviewsActions]);

    return (
        <Box>
            {!PAGE_BTNS.includes(tabKey) && (
            <Box sx={{ p: 2, pb: 0 }}>
                <Box sx={{ ...card, px: 1 }}>
                    <Tabs
                        value={index < 0 ? false : index}
                        onChange={(_, i) => setParams({ tab: visibleTabs[i].key }, { replace: true })}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 52,
                            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: PRIMARY },
                            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: 13.5, color: '#64748B', minHeight: 52, px: 2 },
                            '& .Mui-selected': { color: `${PRIMARY} !important` },
                        }}
                    >
                        {visibleTabs.map((t) => (
                            <Tab
                                key={t.key}
                                iconPosition="start"
                                label={t.label}
                                icon={
                                    t.key === 'interviews' && kpi.awaiting > 0 ? (
                                        <Badge
                                            badgeContent={kpi.awaiting}
                                            max={99}
                                            sx={{ '& .MuiBadge-badge': { bgcolor: '#E11D48', color: '#fff', fontSize: 9.5, fontWeight: 800, minWidth: 16, height: 16 } }}
                                        >
                                            <t.icon sx={{ fontSize: 18 }} />
                                        </Badge>
                                    ) : (
                                        <t.icon sx={{ fontSize: 18 }} />
                                    )
                                }
                            />
                        ))}
                    </Tabs>
                </Box>
            </Box>
            )}

            {tabKey === 'categories' && <CategoriesTab setActions={setCategoriesActions} />}
            {tabKey === 'overview' && <OverviewTab setActions={setOverviewActions} />}
            {tabKey === 'interviews' && <InterviewsTab setActions={setInterviewsActions} />}
            {tabKey === 'vacancies' && <VacanciesTab setActions={setVacanciesActions} />}
            {tabKey === 'candidates' && <CandidatesTab setActions={setCandidatesActions} />}
            {tabKey === 'careers' && <CareersSettingsTab />}
            {tabKey === 'activity' && <ActivityTab />}
        </Box>
    );
}
